import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
    SpeakerWaveIcon,
    MicrophoneIcon,
    LanguageIcon,
    UserIcon,
    PlayIcon,
    DocumentTextIcon,
  } from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      name: 'Translate Audio',
      description: 'Instantly translate audio content between multiple languages with AI-powered accuracy and natural-sounding results.',
      icon: LanguageIcon,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      name: 'Voice Cloning',
      description: 'Create realistic voice clones with just a few minutes of sample audio. Perfect for content creation and personalization.',
      icon: UserIcon,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-100 dark:bg-purple-900',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      name: 'Text To Speech',
      description: 'Convert any text into natural-sounding speech with multiple voice options, languages, and customizable settings.',
      icon: SpeakerWaveIcon,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-100 dark:bg-green-900',
      iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      name: 'Speech To Text',
      description: 'Accurately transcribe audio and video content into text with support for multiple languages and formats.',
      icon: MicrophoneIcon,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-100 dark:bg-orange-900',
      iconColor: 'text-orange-600 dark:text-orange-400',
    },
  ];

  const useCases = [
    {
      title: 'Content Creation',
      description: 'Create multilingual content, podcasts, and videos with AI-generated voices and translations.',
      icon: PlayIcon,
    },
    {
      title: 'Accessibility',
      description: 'Make content accessible with text-to-speech and speech-to-text capabilities for all users.',
      icon: DocumentTextIcon,
    },
    {
      title: 'Global Communication',
      description: 'Break language barriers with real-time audio translation and voice synthesis.',
      icon: LanguageIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Navigation */}
      <nav className="relative px-4 py-6 flex justify-between items-center bg-white dark:bg-gray-800 shadow-sm">
        <div className="text-2xl font-bold text-gray-900 dark:text-white">
          Prime AI
        </div>
        <div className="space-x-4">
          {isAuthenticated ? (
            <Link
              to="/app"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Go to App
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Get Started
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative px-4 py-20 mx-auto max-w-7xl">
        <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                Advanced{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    AI Audio
                </span>{' '}
                Solutions
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto">
                Transform your audio experience with cutting-edge AI technology. From voice cloning to real-time translation, 
                unlock the power of intelligent audio processing.
            </p>
          <div className="space-x-4">
            {!isAuthenticated && (
              <>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-block"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/login"
                  className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-block"
                >
                  Sign In
                </Link>
              </>
            )}
            {isAuthenticated && (
              <Link
                to="/app"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-block"
              >
                Go to Dashboard
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
        <div className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Powerful AI Audio Features
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Experience the next generation of audio AI technology with our comprehensive suite of tools.
            </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
                <div key={feature.name} className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className={`mx-auto h-16 w-16 ${feature.bgColor} rounded-lg flex items-center justify-center mb-4 group-hover:shadow-lg transition-shadow`}>
                    <feature.icon className={`h-8 w-8 ${feature.iconColor}`} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {feature.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                </p>
                </div>
            ))}
            </div>
        </div>
        </div>

        {/* Use Cases Section */}
        <div className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Perfect for Every Use Case
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                From content creators to enterprises, our AI audio solutions adapt to your needs.
            </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase) => (
                <div key={useCase.title} className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                    <useCase.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {useCase.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                    {useCase.description}
                </p>
                </div>
            ))}
            </div>
        </div>
        </div>

      {/* CTA Section */}
        <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Audio?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
            Join thousands of creators and businesses using Prime AI to revolutionize their audio content.
            </p>
            {!isAuthenticated && (
            <div className="space-x-4">
                <Link
                to="/register"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-block"
                >
                Start Free Trial
                </Link>
                <Link
                to="/login"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-block"
                >
                View Demo
                </Link>
            </div>
            )}
        </div>
        </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="text-2xl font-bold mb-4">Prime AI</div>
          <p className="text-gray-400 mb-4">
            Empowering the future with artificial intelligence.
          </p>
          <p className="text-gray-500 text-sm">
            © 2024 Prime AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
